import { GenericApiResponse } from '../../shared/interfaces/generics';
import { appConfig } from '../../request/app.config';
import { courierService } from '../../request/services/courier.service';
import {
  BACKGROUND_NETWORK_REQ_UI_OPTIONS,
  DEFAULT_NETWORK_REQ_OPTIONS,
  NETWORK_REQ_OPTIONS_WITH_TOAST
} from '../../request/constants/request-options';
import { RawSource, RawSourceType } from '../../shared/interfaces/source';
import { NetworkRequestOptions } from '../../request/interfaces/request-options';
import { AsyncTaskResponse, AsyncTaskStatusResponse } from '../../shared/interfaces/async-task';
import {
  EntityDefaultName,
  TemplateRawForm
} from '../../template-form/interfaces/template-raw-form';
import { RawAuthConsentURL, RawDummyTokens } from '../../shared/interfaces/auth';
import { FlatFileObjectConfigFormRawTemplate } from '../../pipelines/ObjectsConfiguration/components/FlatFileObjectsConfig/interfaces/flat-file-object-config-form-template';
import { RawEntityDisplayConfig } from '../../shared/interfaces/entity-display-config';
import { RawIntegration } from '../../shared/interfaces/integration';

export const SourceAPI = {
  sourcesURL: appConfig.getSourcesURL(),
  taskFlowURL: appConfig.getTaskFlowURL(),

  getSourceTypes(options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.sourcesURL}/types`;

    return courierService.get<GenericApiResponse<RawSourceType[]>>(requestURL, options);
  },

  getSources(options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = this.sourcesURL;

    return courierService.get<GenericApiResponse<RawSource[]>>(requestURL, {
      ...options,
      networkOptions: {
        params: {
          include_deleted: false
        }
      }
    });
  },

  getSourcesByTeamId(teamId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.sourcesURL}/${teamId}`;

    return courierService.get<GenericApiResponse<RawSource[]>>(requestURL, options);
  },

  validateSourceName(sourceName: string, config = {}) {
    const requestURL = `${this.sourcesURL}/validate/source-name`;
    const params = {
      source_name: sourceName
    };

    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    return courierService.post<GenericApiResponse<{ valid: boolean; reason?: string }>>(
      requestURL,
      params,
      options
    );
  },

  createSource(params: object, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.sourcesURL}/create`;

    return courierService.post<GenericApiResponse<RawSource>>(requestURL, params, options);
  },

  getSource(sourceId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.sourcesURL}/${sourceId}`;

    return courierService.get<GenericApiResponse<RawSource>>(requestURL, options);
  },

  getDefaultSourceName(
    sourceType: string,
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ): Promise<GenericApiResponse<EntityDefaultName>> {
    const requestURL = `${this.sourcesURL}/${sourceType}/default-name`;

    return courierService.get<GenericApiResponse<EntityDefaultName>>(requestURL, options);
  },

  getSourceIntegrations(sourceId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.sourcesURL}/${sourceId}/integrations`;

    return courierService.get<GenericApiResponse<RawIntegration[]>>(requestURL, {
      ...options,
      networkOptions: {
        params: {
          include_deleted: false
        }
      }
    });
  },

  deleteSource(sourceId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.sourcesURL}/${sourceId}`;

    return courierService.delete<GenericApiResponse<RawSource>>(requestURL, options);
  },

  getSourceConfigTemplateByType(sourceType: string, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestUrl = `${this.sourcesURL}/${sourceType}/config-template`;

    return courierService.get<GenericApiResponse<TemplateRawForm>>(requestUrl, options);
  },

  getSourceConfigTemplateWithFormData(sourceId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.sourcesURL}/${sourceId}/config/edit`;

    return courierService.get<GenericApiResponse<TemplateRawForm>>(requestURL, options);
  },

  getTestConnectionTaskIdForNewSource(sourceType: string, params: unknown) {
    const requestUrl = `${this.sourcesURL}/test`;

    const config = { ...(params as unknown as { config: object }).config };

    const payload = {
      source_type: sourceType,
      config
    };

    return courierService.post<GenericApiResponse<AsyncTaskResponse>>(requestUrl, payload);
  },

  getTestConnectionTaskIdForExistingSource(sourceId: number) {
    const requestUrl = `${this.sourcesURL}/${sourceId}/test`;

    return courierService.post<GenericApiResponse<AsyncTaskResponse>>(requestUrl, {});
  },

  getTestConnectionTaskIdForSourceEdit(sourceId: number, sourceConfig: object) {
    const requestUrl = `${this.sourcesURL}/${sourceId}/edit/test`;

    return courierService.post<GenericApiResponse<AsyncTaskResponse>>(requestUrl, sourceConfig);
  },

  getTestConnectionStatus(
    taskFlowId: string
  ): Promise<GenericApiResponse<AsyncTaskStatusResponse>> {
    const requestUrl = `${this.taskFlowURL}/${taskFlowId}/status`;

    return courierService.get<GenericApiResponse<AsyncTaskStatusResponse>>(requestUrl);
  },

  getConsentUrl(
    baseURL: string,
    provider: string,
    next: string,
    cancel: string,
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ) {
    const requestURL = `${baseURL}/oauth/connect/${provider}?next=${next}&cancel=${cancel}`;

    return courierService.get<GenericApiResponse<RawAuthConsentURL>>(requestURL, options);
  },

  getToken(
    baseURL: string,
    provider: string,
    created_ts: number,
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ) {
    const requestURL = `${baseURL}/oauth/token/${provider}?created_ts=${created_ts}`;

    return courierService.get<GenericApiResponse<RawDummyTokens>>(requestURL, options);
  },

  getSourceObjectConfigFormTemplate(sourceId: number) {
    const requestURL = `${this.sourcesURL}/${sourceId}/object-config-template`;

    return courierService.get<GenericApiResponse<FlatFileObjectConfigFormRawTemplate>>(requestURL);
  },

  triggerSourceObjectInferSchema(sourceId: number, params: unknown) {
    const requestURL = `${this.sourcesURL}/${sourceId}/trigger-create-catalog-task-flow`;

    return courierService.post<GenericApiResponse<AsyncTaskResponse>>(requestURL, params);
  },

  getInferSchemaStatus({ taskFlowId, sourceId }: { taskFlowId: string; sourceId: number }) {
    const requestUrl = `${this.sourcesURL}/${sourceId}/create-catalog-task-flow/status/${taskFlowId}`;

    return courierService.get<
      GenericApiResponse<
        AsyncTaskStatusResponse<{
          source_object_id: string;
        }>
      >
    >(requestUrl);
  },

  getSourceDisplayConfigDetails(sourceId: number) {
    const requestUrl = `${this.sourcesURL}/${sourceId}/display-config`;

    return courierService.get<GenericApiResponse<RawEntityDisplayConfig<RawSourceType>>>(
      requestUrl
    );
  },

  refreshSchema(sourceId: number) {
    return courierService.post(
      `${this.sourcesURL}/${sourceId}/refresh`,
      {},
      NETWORK_REQ_OPTIONS_WITH_TOAST
    );
  },

  getCatalogRefreshStatus(sourceId: number) {
    return courierService.get<
      GenericApiResponse<{
        in_progress: boolean;
      }>
    >(`${this.sourcesURL}/${sourceId}/refresh/status`, DEFAULT_NETWORK_REQ_OPTIONS);
  },

  getCatalogLastRefreshedAt(sourceId: number) {
    return courierService.get<GenericApiResponse<{ refreshed_at: number | null }>>(
      `${this.sourcesURL}/${sourceId}/catalog`,
      DEFAULT_NETWORK_REQ_OPTIONS
    );
  },

  editSource(sourceId: number, sourceConfig: object) {
    const requestUrl = `${this.sourcesURL}/${sourceId}/config/edit`;

    return courierService.post<GenericApiResponse<RawSource>>(requestUrl, sourceConfig);
  }
};
